
import React, { useState, useEffect } from 'react';
import { Youtube, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchLatestYouTubeVideos, YouTubeVideo, formatPublishDate } from '@/lib/youtube';

interface YouTubeVideoGridProps {
  maxVideos?: number;
}

const YouTubeVideoGrid: React.FC<YouTubeVideoGridProps> = ({ maxVideos = 6 }) => {
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      setError(false);
      try {
        const fetchedVideos = await fetchLatestYouTubeVideos(maxVideos);
        setVideos(fetchedVideos);
      } catch (err) {
        console.error('Error fetching videos for grid:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [maxVideos]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array(maxVideos).fill(0).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="w-full aspect-video rounded-lg" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (error || videos.length === 0) {
    return (
      <div className="text-center py-12 bg-adv-charcoal/30 rounded-lg">
        <Video size={48} className="text-adv-lightGray mx-auto mb-4 opacity-50" />
        <h3 className="text-xl font-medium mb-2 text-white">Videos coming soon</h3>
        <p className="text-adv-lightGray mb-6">Subscribe to @ExodusADV on YouTube</p>
        <Button 
          onClick={() => window.open('https://youtube.com/@Exodusadv', '_blank')}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Youtube size={18} className="mr-2" />
          Subscribe on YouTube
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {videos.map((video) => (
        <div key={video.id} className="bg-adv-charcoal rounded-lg overflow-hidden border border-adv-darkGray/30 group">
          <a 
            href={`https://www.youtube.com/watch?v=${video.id}`} 
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={video.thumbnail.url}
                alt={video.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-70"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-red-600/80 rounded-full p-3 transform transition-all duration-300 group-hover:scale-110 group-hover:bg-red-600">
                  <Youtube size={24} className="text-white" />
                </div>
              </div>
            </div>
          </a>
          
          <div className="p-4">
            <a 
              href={`https://www.youtube.com/watch?v=${video.id}`} 
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <h3 className="text-white font-medium line-clamp-2 group-hover:text-adv-orange transition-colors mb-2">
                {video.title}
              </h3>
            </a>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-adv-lightGray">
                {formatPublishDate(video.publishedAt)}
              </span>
              
              <a 
                href={`https://www.youtube.com/watch?v=${video.id}`} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1"
              >
                Watch on YouTube
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default YouTubeVideoGrid;
